import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import EachNotification from "./EachNotification";
import { RootState } from "../../store";
import { connect, ConnectedProps } from "react-redux";
import {
  approveServiceRequest,
  disapproveServiceRequest,
  readNotification,
} from "@/store/app/app.actions";
import { Skeleton } from "..";
import Modal from "../Modal/Modal";
import UserRequest from "./UserRequest";
import {
  setCurrentNotification,
  setNotifications,
  updateModal,
} from "@/store/app/app.slice";
import { useClickOutside } from "../../utils";
import { useNotificationObserver } from "../../hooks/useNotificationObserver";
import { getNotifications } from "@/store/app/app.actions";

const Notifications = ({
  notifications,
  readNotification,
  setCurrentNotification,
  user,
  setNotifications,
  bunchProductList,
  bunchBusinessList,
  approveServiceRequest,
  disapproveServiceRequest,
  userList,
  getNotifications,
  onClose,
  allHostsList,
  initialTab,
}: Props) => {
  const [activeTab, setActiveTab] = useState(initialTab ?? 0);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [showUnreadOnly, setShowUnreadOnly] = useState<boolean>(false);
  const [showMoreOptions, setShowMoreOptions] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const ref = useRef<HTMLDivElement>(null);
  const notificationsContainerRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const analyticsLocation = pathname.startsWith("/discover")
    ? "discover"
    : "content";

  useClickOutside(ref, () => {
    setShowMoreOptions(false);
  });

  useNotificationObserver({
    notifications,
    user,
    readNotification,
    isActive: true,
    containerRef: notificationsContainerRef,
  });

  const handleOpenRequestModal = useCallback((message: any) => {
    setSelectedMessage(message);
    setRequestModalOpen(true);
  }, []);

  const handleCloseRequestModal = useCallback(() => {
    setRequestModalOpen(false);
    setSelectedMessage(null);
    setCurrentNotification(null);
  }, [setCurrentNotification]);

  const handleDeleteNotification = useCallback(
    (notificationId: string) => {
      setNotifications({
        list: notifications.filter(
          (notification: any) => notification.id !== notificationId
        ),
      });
    },
    [notifications, setNotifications]
  );

  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoading(true);
      try {
        await getNotifications({
          foreign_keys: ["meta", "state"],
        });
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();
  }, [getNotifications]);

  useEffect(() => {
    if (initialTab !== undefined) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const handleMarkAllAsRead = useCallback(
    (type: string) => {
      if (user && user.id && notifications) {
        const currentTabNotifications = notifications.filter((message: any) => {
          if (activeTab === 0) {
            return message?.meta?.type === 5;
          } else if (activeTab === 1) {
            return message?.meta?.type === 6;
          } else {
            return message?.meta?.type !== 5 && message?.meta?.type !== 6;
          }
        });
        const allIds = currentTabNotifications.map(
          (notification: any) => notification.id
        );
        const unreadIds = currentTabNotifications
          .filter((notification: any) => notification.read === 0)
          .map((notification: any) => notification.id);
        if (type === "delete" ? allIds.length > 0 : unreadIds.length > 0) {
          readNotification({
            ids: type === "delete" ? allIds : unreadIds,
            read: 1,
            status: type === "delete" ? -1 : 0,
          }).then(() => {
            if (type === "delete") {
              setNotifications({
                list: notifications.filter(
                  (notification: any) => !allIds.includes(notification.id)
                ),
              });
            }
          });
        }
      }
      setShowMoreOptions(false);
    },
    [user, notifications, activeTab, readNotification, setNotifications]
  );

  const filteredMessages = useMemo(() => {
    return (
      notifications?.filter((message: any) => {
        if (showUnreadOnly && message.read === 1) {
          return false;
        }
        if (activeTab === 0) {
          return message?.meta?.type === 5;
        } else if (activeTab === 1) {
          return message?.meta?.type === 6;
        } else {
          return message?.meta?.type !== 5 && message?.meta?.type !== 6;
        }
      }) || []
    );
  }, [notifications, activeTab, showUnreadOnly]);

  const renderSkeletonNotifications = useMemo(() => {
    return [...Array(12)].map((_, index) => (
      <div key={index} className="notification-item p-3 border-bottom rounded">
        <div className="d-flex justify-content-between align-items-center">
          <div
            className="d-flex flex-row gap-3"
            style={{ width: "calc(100% - 90px)" }}
          >
            <div className="d-flex align-items-center justify-content-center rounded-circle">
              <Skeleton type="avatar" width="35px" height="35px" circle />
            </div>
            <div className="anchor flex-grow-1 d-flex flex-column gap-1">
              <Skeleton
                type="text"
                width="80%"
                height="16px"
                className="mb-1"
              />
              <Skeleton type="text" width="60%" height="12px" />
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Skeleton type="text" width="24px" height="24px" />
            <Skeleton type="text" width="24px" height="24px" />
            <Skeleton type="text" width="24px" height="24px" />
          </div>
        </div>
      </div>
    ));
  }, []);

  const navTabs = [
    { label: "Uploads", index: 0 },
    { label: "Publish", index: 1 },
    // { label: "Requests", index: 2 },
  ];
  return (
    <div
      className="notifications-container"
      ref={notificationsContainerRef}
      style={{ marginTop: "-14px" }}
    >
      <div
        className="d-flex justify-content-between align-items-center position-sticky bg-white z-10 border-bottom py-2"
        style={{ top: "-14px" }}
      >
        <ul className="nav rounded-pill">
          {navTabs.map((item) => {
            return (
              <li key={item.index} className="nav-item">
                <button
                  className={`nav-link rounded-pill ${
                    activeTab === item.index ? "text-white bg-primary" : ""
                  }`}
                  onClick={() => setActiveTab(item.index)}
                >
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="d-flex align-items-center gap-2">
          <span className="">Unread</span>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="unreadSwitch"
              checked={showUnreadOnly}
              onChange={(e) => setShowUnreadOnly(e.target.checked)}
            />
          </div>
          <i
            className="bi bi-three-dots-vertical cursor-pointer fs-4 iconColor"
            ref={ref}
            onClick={() => setShowMoreOptions(!showMoreOptions)}
          ></i>
          <ul
            className={`dropdown-menu ${showMoreOptions ? "show" : ""}`}
            style={{ top: "40px", right: "20px" }}
          >
            <li
              onClick={() => handleMarkAllAsRead("read")}
              className="text-capitalize"
            >
              <span className="dropdown-item">
                <i className={`bi bi-check-circle me-2 `}></i>
                Mark all as read
              </span>
            </li>
            <li
              onClick={() => {
                handleMarkAllAsRead("delete");
                setShowMoreOptions(false);
              }}
              className="text-capitalize"
            >
              <span className="dropdown-item">
                <i className={`bi bi-trash me-2 `}></i>
                Delete All
              </span>
            </li>
          </ul>
        </div>
      </div>
      {isLoading ? (
        renderSkeletonNotifications
      ) : filteredMessages?.length ? (
        filteredMessages?.map((message: any, index: any) => (
          <div
            className="catRow"
            key={`${activeTab}-${message.id}-${index}`}
            data-notification-id={message.id}
          >
            <EachNotification
              message={message}
              onOpenRequestModal={handleOpenRequestModal}
              setCurrentNotification={setCurrentNotification}
              readNotification={readNotification}
              user={user}
              productList={bunchProductList}
              businessList={bunchBusinessList}
              approveServiceRequest={approveServiceRequest}
              disapproveServiceRequest={disapproveServiceRequest}
              userList={userList}
              onDeleteNotification={handleDeleteNotification}
              onClose={onClose}
              allHostsList={allHostsList}
              analyticsLocation={analyticsLocation}
            />
          </div>
        ))
      ) : (
        <p className="text-center p-5">No notifications available.</p>
      )}
      {requestModalOpen && selectedMessage && (
        <Modal
          isOpen={requestModalOpen}
          onClose={handleCloseRequestModal}
          title="Notification Centre"
          width="100vw"
        >
          <UserRequest
            message={selectedMessage.meta.meta}
            onClose={handleCloseRequestModal}
          />
        </Modal>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  notifications: state.app.notifications.list,
  userList: state.users.userList.list,
  user: state.users.singleCurrentUser,
  bunchProductList: state.constants.bunchProductList.list,
  bunchBusinessList: state.constants.bunchBusinessList.list,
  allHostsList: state.constants.allHostsList.list,
});

const mapDispatch = {
  setCurrentNotification,
  readNotification,
  setNotifications,
  updateModal,
  approveServiceRequest,
  disapproveServiceRequest,
  getNotifications,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & {
  onClose: () => void;
  initialTab?: number;
};

export default connector(Notifications);
